import { useEffect, useState, useLayoutEffect } from "react";
import * as XLSX from "xlsx";
import { useLazyQuery } from "@apollo/client";

import "./JobListPage.scss";
import { SortSettings } from "../../../component/sortable-table/SortableTable";
import { useDarkModeCheck } from "../DashboardLayout";
import {
  inputDateFormat,
  previousMonthRangeSelect,
  sortTableFunction,
} from "../../../service/utils/functions";
import {
  excelFileExtension,
  excelFileType,
  jobStatusSelectorList,
  SortOrder,
  tableIssuesHeader,
  tableJobsHeader,
  tableTippingJobsHeader,
} from "../../../service/utils/constants";
import {
  GET_CUSTOMERS,
  GET_JOBS_ISSUES,
  GET_JOBS_TIPPING,
} from "../../../service/graphql/query";
import {
  CustomerDataEntry,
  JobStatusDataEntry,
  SiteDataEntry,
  WasteTypeDataEntry,
} from "../../../service/utils/interfaces";
import * as Storage from "../../../service/storage";
import {
  customerListFormat,
  useCustomerFilters,
  useRequestGuard,
} from "../../../service/hooks/customerData";
import {
  jobListFormat,
  issueListFormat,
  jobTippingListFormat,
} from "../../../service/utils/parsers";
import JobListUI from "./JobListUI";
import { trackDownload } from "../../../service/matomo";

const FileSaver = require("file-saver");

const JobListPage = () => {
  const { darkMode } = useDarkModeCheck();

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(() =>
    previousMonthRangeSelect()
  );
  const [startDate, endDate] = dateRange;

  const [loadingData, setLoadingData] = useState(true);
  const [customerList, setCustomerList] = useState<CustomerDataEntry[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerDataEntry>();
  const [sitesPerCustomer, setSitesPerCustomer] = useState<SiteDataEntry[]>([]);
  const [selectedSites, setSelectedSites] = useState<SiteDataEntry[]>();
  const [wasteTypesPerCustomer, setWasteTypePerCustomer] = useState<
    WasteTypeDataEntry[]
  >([]);
  const [selectedWasteTypes, setSelectedWasteTypes] =
    useState<WasteTypeDataEntry[]>();
  const [selectedStatus, setSelectedStatus] = useState<JobStatusDataEntry[]>();

  const [headerJobsLine, setHeaderJobsLine] = useState<string[]>([]);
  const [jobsContent, setJobsContent] = useState<(string | number)[][]>([]);
  const [filteredJobsContent, setFilteredJobsContent] = useState<
    (string | number)[][]
  >([]);
  const [headerIssuesLine, setHeaderIssuesLine] = useState<string[]>([]);
  const [issuesContent, setIssuesContent] = useState<(string | number)[][]>([]);
  const [filteredIssuesContent, setFilteredIssuesContent] = useState<
    (string | number)[][]
  >([]);
  const [sortSettings, setSortSettings] = useState<SortSettings>();
  const [searchParam, setSearchParam] = useState<string>("");
  const [reordering, setReordering] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [isTippingUser, setIsTippingUser] = useState(false);

  const [getCustomers, { data: customersData }] = useLazyQuery(GET_CUSTOMERS);
  const loadCustomerFilters = useCustomerFilters();
  const isNewJobsRequest = useRequestGuard();
  // Customer whose sites and waste types are loaded into the filter state.
  // Job data waits for it, so no call goes out with the previous customer's sites.
  const [filtersCustomerId, setFiltersCustomerId] = useState<number>();
  const [isDateRangeReady, setIsDateRangeReady] = useState(false);
  const [getJobIssueLists, { data: jobsIssuesData }] =
    useLazyQuery(GET_JOBS_ISSUES);
  const [getJobTippingLists, { data: jobsTippingData }] =
    useLazyQuery(GET_JOBS_TIPPING);

  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const width = window.innerWidth;
    setWidth(width);
  }, []);

  useEffect(() => {
    Storage.FILTER_DATA.get().then((filter) => {
      const start =
        filter.startDate.length > 0 ? new Date(filter.startDate) : null;
      const end = filter.endDate.length > 0 ? new Date(filter.endDate) : null;
      if (start && end) setDateRange([start, end]);
      else {
        const updateRange = previousMonthRangeSelect();
        setDateRange(updateRange);
      }
      setIsDateRangeReady(true);
    });

    Storage.USER_DATA.get()
      .then(async (obj) => {
        if (obj.length > 0) {
          setCustomerList(obj);
          const filter = await Storage.FILTER_DATA.get();
          const idx = filter.customerIndex >= 0 ? filter.customerIndex : 0;
          const fetchedTippingUserData = obj[0].category === "Tipping";
          setIsTippingUser(fetchedTippingUserData);
          setHeaderJobsLine(
            fetchedTippingUserData ? tableTippingJobsHeader : tableJobsHeader
          );
          setHeaderIssuesLine(
            fetchedTippingUserData ? tableTippingJobsHeader : tableIssuesHeader
          );
          setSelectedCustomer(obj[idx]);
        } else getCustomers({ context: { apiName: "middleware" } });
      })
      .catch((_) => getCustomers({ context: { apiName: "middleware" } }));

    const handleWindowResize = () => {
      const width = window.innerWidth;
      setWidth(width);
    };
    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, [getCustomers]);

  useEffect(() => {
    if (!selectedCustomer) return;
    let isCurrent = true;

    (async () => {
      // Tipping customers have no site or waste type filters.
      const customer: CustomerDataEntry = isTippingUser
        ? { ...selectedCustomer, sites: [], wastes: [] }
        : await loadCustomerFilters(selectedCustomer);
      const filter = await Storage.FILTER_DATA.get();
      if (!isCurrent) return;

      const sites = customer.sites ?? [];
      const wastes = customer.wastes ?? [];
      setSitesPerCustomer(sites);
      setWasteTypePerCustomer(wastes);
      setSelectedSites(
        sites.filter((_, idx) => filter.siteIndex?.includes(idx))
      );
      setSelectedWasteTypes(
        wastes.filter((_, idx) => filter.wasteTypeIndex?.includes(idx))
      );
      setSelectedStatus([]);
      setFiltersCustomerId(selectedCustomer.value);
    })();

    return () => {
      isCurrent = false;
    };
  }, [selectedCustomer, isTippingUser, loadCustomerFilters]);

  useEffect(() => {
    if (
      isDateRangeReady &&
      selectedCustomer &&
      filtersCustomerId === selectedCustomer.value &&
      selectedSites &&
      selectedWasteTypes &&
      selectedStatus &&
      dateRange &&
      dateRange[0] &&
      dateRange[1]
    ) {
      let siteIdList: any[] = [];
      if (selectedSites.length > 0)
        siteIdList = selectedSites.map((el: any) => el.value);
      else siteIdList = sitesPerCustomer.map((el: any) => el.value);

      let wasteTypeIdList: any[] = [];
      if (selectedWasteTypes.length > 0)
        wasteTypeIdList = selectedWasteTypes.map((el: any) => el.value);
      else wasteTypeIdList = wasteTypesPerCustomer.map((el: any) => el.value);

      let statusList: any[] = [];
      if (selectedStatus.length > 0)
        statusList = selectedStatus.map((el: any) => el.value);
      else statusList = jobStatusSelectorList.map((status) => status.value);

      const input = isTippingUser
        ? {
            documentId: selectedCustomer?.value,
            startDate: inputDateFormat(dateRange[0] as Date),
            endDate: inputDateFormat(dateRange[1] as Date),
          }
        : {
            siteList: siteIdList,
            wasteList: wasteTypeIdList,
            statusList: statusList,
            startDate: inputDateFormat(dateRange[0] as Date),
            endDate: inputDateFormat(dateRange[1] as Date),
          };
      // Skip the call when the effect re-runs with the same filters, e.g. when
      // the filter row re-sends an unchanged date range.
      if (!isNewJobsRequest({ isTippingUser, input })) return;

      setLoadingData(true);
      if (!isTippingUser) {
        getJobIssueLists({
          variables: { input },
          context: { apiName: "middleware" },
        });
      } else {
        getJobTippingLists({
          variables: { input },
          context: { apiName: "middleware" },
        });
      }
    }
  }, [
    isDateRangeReady,
    selectedCustomer,
    filtersCustomerId,
    isNewJobsRequest,
    selectedSites,
    selectedWasteTypes,
    selectedStatus,
    sitesPerCustomer,
    wasteTypesPerCustomer,
    dateRange,
    isTippingUser,
    getJobIssueLists,
    getJobTippingLists,
  ]);

  useEffect(() => {
    if (customersData && customersData.getCustomers) {
      manageCustomersData(customersData);
    }
  }, [customersData]);

  const manageCustomersData = async (customersData: any) => {
    // Sites and waste types are loaded later, for the selected customer only.
    const tempCustomerList = customerListFormat(customersData.getCustomers);
    const fetchedTippingUserData = tempCustomerList[0]?.category === "Tipping";
    setIsTippingUser(fetchedTippingUserData);
    setHeaderJobsLine(
      fetchedTippingUserData ? tableTippingJobsHeader : tableJobsHeader
    );
    setHeaderIssuesLine(
      fetchedTippingUserData ? tableTippingJobsHeader : tableIssuesHeader
    );
    setCustomerList(tempCustomerList);
    const filter = await Storage.FILTER_DATA.get();
    const idx = filter.customerIndex >= 0 ? filter.customerIndex : 0;
    setSelectedCustomer(tempCustomerList[idx]);
    Storage.USER_DATA.set(tempCustomerList);
  };

  useEffect(() => {
    if (jobsIssuesData && jobsIssuesData.getJobIssueDocList) {
      manageJobIssuesData(jobsIssuesData);
    }
  }, [jobsIssuesData]);

  const manageJobIssuesData = (jobsIssuesData: any) => {
    const parsedJobs = jobListFormat(jobsIssuesData.getJobIssueDocList.jobs);
    const parsedIssues = issueListFormat(
      jobsIssuesData.getJobIssueDocList.issues
    );
    setJobsContent(parsedJobs);
    setFilteredJobsContent(parsedJobs);
    setIssuesContent(parsedIssues);
    setFilteredIssuesContent(parsedIssues);
    setLoadingData(false);
  };

  useEffect(() => {
    if (jobsTippingData && jobsTippingData.getJobTippingList) {
      manageJobTippingData(jobsTippingData);
    }
  }, [jobsTippingData]);

  const manageJobTippingData = (jobsTippingData: any) => {
    const parsedJobs = jobTippingListFormat(
      jobsTippingData.getJobTippingList.jobs
    );
    const parsedCardboard = jobTippingListFormat(
      jobsTippingData.getJobTippingList.cardboard
    );
    setJobsContent(parsedJobs);
    setFilteredJobsContent(parsedJobs);
    setIssuesContent(parsedCardboard);
    setFilteredIssuesContent(parsedCardboard);
    setLoadingData(false);
  };

  const changeSortAction = (col: number) => {
    setSortSettings({
      column: col,
      order:
        sortSettings?.column === col &&
        sortSettings?.order === SortOrder.Ascending
          ? SortOrder.Descending
          : SortOrder.Ascending,
    });
    setReordering(true);
  };

  const changeSearchParam = (val: string) => {
    setSearchParam(val);
  };

  const openIssuesForJobNumber = (jobNumber: string) => {
    if (!jobNumber || isTippingUser) return;
    setSearchParam(jobNumber);
    setCurrentTab(1);
  };

  useEffect(() => {
    if (sortSettings && reordering) {
      setReordering(false);
      if (currentTab > 0) {
        const reorderedTable = issuesContent.sort((a, b) =>
          sortTableFunction(a, b, sortSettings)
        );
        setFilteredIssuesContent(reorderedTable);
      } else {
        const reorderedTable = jobsContent.sort((a, b) =>
          sortTableFunction(a, b, sortSettings)
        );
        setFilteredJobsContent(reorderedTable);
      }
    }
  }, [sortSettings, reordering, currentTab, issuesContent, jobsContent]);

  const downloadExcelTable = () => {
    const content =
      currentTab > 0
        ? filteredIssuesContent
        : filteredJobsContent.map((row) => {
            if (isTippingUser) return row;
            const lastItem = row[row.length - 1];
            if (typeof lastItem === "string") {
              row[row.length - 1] =
                lastItem.length > 0 ? "<document link>" : "";
            } else {
              row[row.length - 1] = "";
            }
            return row;
          });
    const headerLine = currentTab > 0 ? headerIssuesLine : headerJobsLine;
    const tableToPrint = [[], ...content];
    const ws = XLSX.utils.aoa_to_sheet(tableToPrint);
    XLSX.utils.sheet_add_aoa(ws, [headerLine], { origin: "A1" });
    const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: excelFileType });
    const fileName =
      currentTab > 0
        ? isTippingUser
          ? "Cardboards"
          : "Job Issues"
        : "Job List" + excelFileExtension;
    FileSaver.saveAs(data, fileName);
    trackDownload(fileName, "table-export");
  };

  const resetTable = () => {
    changeSearchParam("");
    setSortSettings(undefined);
  };

  return (
    <JobListUI
      isTippingUser={isTippingUser}
      customerList={customerList}
      sitesPerCustomer={sitesPerCustomer}
      wasteTypesPerCustomer={wasteTypesPerCustomer}
      startDate={startDate}
      endDate={endDate}
      currentTab={currentTab}
      loadingData={loadingData}
      headerJobsLine={headerJobsLine}
      filteredJobsContent={filteredJobsContent}
      sortSettings={sortSettings}
      searchParam={searchParam}
      headerIssuesLine={headerIssuesLine}
      filteredIssuesContent={filteredIssuesContent}
      resetTable={resetTable}
      setSelectedCustomer={setSelectedCustomer}
      setSelectedSites={setSelectedSites}
      setSelectedWasteTypes={setSelectedWasteTypes}
      setSelectedStatus={setSelectedStatus}
      setDateRange={setDateRange}
      setCurrentTab={setCurrentTab}
      onJobNumberClick={openIssuesForJobNumber}
      downloadExcelTable={downloadExcelTable}
      changeSortAction={changeSortAction}
      changeSearchParam={changeSearchParam}
      darkMode={darkMode}
      width={width}
    />
  );
};

export default JobListPage;
